// internal imports
import React, { useMemo, useState } from "react";
import { useDocumentTitle, useGetService, usePastAssignedDetails } from "../../../hooks";

// Third party
import _ from "lodash";
import { DataGrid } from "@mui/x-data-grid";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";

// style
import "../Assignment.scss";
import { PastToolbar } from "./PastToolbar";
import moment from "moment";
import { addDays, removeSecondsFromDateTime } from "../../../utils/date.utils";
import { getUserData, serviceRequestTypeToLabelMap } from "../../../shared/utils";
import { API } from "../../../constants";
import { EServiceRequestType } from "../../../types/global.types";

export const Past = () => {
  useDocumentTitle("Past");

  //Filter set
  const [searchParams, setSearchParams] = useState("");
  const [startDate, setStartDate] = useState<Date>(addDays(new Date(), -7));
  const [endDate, setEndDate] = useState<Date>(addDays(new Date(), -1));

  const vendorId = getUserData()["cognito:username"];

  const startDateFormatted = moment(startDate).format("DD/MM/YYYY");
  const endDateFormatted = moment(endDate).format("DD/MM/YYYY");

  const apiUrl = useMemo(
    () =>
      `${API.CARNIVAL_ADMIN.GET_SERVICE_REQUEST_BY_VENDOR_ID}?vendorId=${vendorId}&startDate=${startDateFormatted}&endDate=${endDateFormatted}`,
    [vendorId, startDateFormatted, endDateFormatted]
  );

  const {
    response,
    isLoading: isRowsLoading,
    refetch: refetchAssinged,
  } = useGetService(
    apiUrl
  );

  const rows = useMemo(() => {
    return response?.data || [];
  }, [response]);

  const handleSearchParam = (text: string) => {
    setSearchParams(text);
  };

  const columnHeaderData = useMemo(() => {
    return [
      {
        field: "tmId",
        headerName: "Crew Id",
        flex: 1,
      },
      {
        field: "FULL_NAME",
        headerName: "Full name",
        flex: 1,
        valueGetter: (value: any, row: any) => {
          return `${row.FirstName || ""} ${row.LastName || ""}` || "";
        },
      },
      {
        field: "arrivalDate",
        headerName: "Arrival / Checkout date",
        flex: 1.5,
        valueGetter: (value: any, row: any) => {
          return removeSecondsFromDateTime(`${row.arrivalDate || ""} ${ row.arrivalTime || ""}` ||  "");
        },
      },
      {
        field: "empName",
        headerName: "Assigned name",
        flex: 1,
        valueGetter: (value: any, row: any) => {
          return row.empName;
        },
      },
      {
        field: "scannedByGreeterOn",
        headerName: "Scanned on",
        flex: 1,
        valueGetter: (value: any, row: any) => {
          const dateValue = row.scannedByGreeterOn || row.updatedOn;
          if (!dateValue) return "";

          const formattedDate = moment(dateValue);
          return formattedDate.isValid()
            ? formattedDate.format("YYYY-MM-DD hh:mm A")
            : "";
        },
      },
      {
        field: "HotelName",
        headerName: "Hotel",
        flex: 1,
        valueGetter: (value: any, row: any) => {
          return row.HotelName;
        },
      },
      {
        field: "requestType",
        headerName: "RequestType",
        flex: 1,
        valueGetter: (value: any, row: any) => {
          return  serviceRequestTypeToLabelMap[row.requestType as EServiceRequestType] || row.requestType;
        },
      },
      {
        field: "Airline",
        headerName: "Flight",
        flex: 1,
        valueGetter: (value: any, row: any) => {
          const airline = row?.OperatingAirlineName;
            const flight = row?.FlightNbr;

            if (!airline || !flight) {
              return "";
            }

            return `${airline} - ${flight}`.trim();
        },
        renderCell: (params: any) => {
          const airline = params?.row?.OperatingAirlineName;
          const flight = params?.row?.FlightNbr;

          if (!airline || !flight) {
            return <></>;
          }

          return <>{`${airline} - ${flight}`}</>;
        },
        exportValueGetter: (params: any) => {
          const airline = params?.row?.OperatingAirlineName;
            const flight = params?.row?.FlightNbr;
            return airline && flight ? `${airline} - ${flight}` : "";
        },
      },
      {
        field: "status",
        headerName: "Scanned",
        flex: 1,
        valueFormatter: (value: any) => {
          return value === "pending" ? "No" : "Yes";
        },
        renderCell: (params: any) => (
          <>
            {params.value !== "pending" ? (
              <CheckCircleIcon
                style={{
                  color: "green",
                  paddingLeft: "20px",
                  paddingTop: "13px",
                }}
              />
            ) : (
              <CancelIcon
                style={{
                  color: "grey",
                  paddingLeft: "20px",
                  paddingTop: "13px",
                }}
              />
            )}
          </>
        ),
      },
    ];
  }, []);

  const rowData = useMemo(() => {
    let data = [...rows];
    if (searchParams) {
      const searchParamsLowerCase = searchParams.toLowerCase();
      data = rows?.filter((item: any) => {
        return (
          item.EmpId?.includes(searchParamsLowerCase) ||
          (item.FirstName?.toLowerCase() || "")?.includes(searchParamsLowerCase) ||
          (item.LastName?.toLowerCase() || "")?.includes(searchParamsLowerCase) ||
          (item.empName?.toLowerCase() || "")?.includes(searchParamsLowerCase) ||
          (item.vendorName?.toLowerCase() || "")?.includes(searchParamsLowerCase) 
        );
      });
    }
    return data;
  }, [searchParams, rows]);

  console.error("Row data:", rowData);

  return (
    <div className="grid-section">
      <DataGrid
        rows={rowData || []}
        sx={{
          minHeight: 400,
          height: rowData.length === 0 ? 400 : "auto",
          marginTop: 2,
        }}
        columns={columnHeaderData}
        hideFooter={true}
        loading={isRowsLoading}
        slots={{
          toolbar: PastToolbar,
        }}
        slotProps={
          {
            toolbar: {
              searchParams,
              handleSearchParam,
              startDate,
              endDate,
              handleStartDate: setStartDate,
              handleEndDate: setEndDate,
              exportOptions: {
                fileName: `Past_Vendor_Staff_Assignment_Report_${(() => {
                  const now = new Date();
                  const monthNames = [
                    "January",
                    "February",
                    "March",
                    "April",
                    "May",
                    "June",
                    "July",
                    "August",
                    "September",
                    "October",
                    "November",
                    "December",
                  ];
                  const month = monthNames[now.getMonth()];
                  const day = now.getDate();
                  const year = now.getFullYear();
                  return `${month}-${day}-${year}`;
                })()}`,
              },
            },
            loadingOverlay: {
              variant: "linear-progress",
              noRowsVariant: "skeleton",
            },
          } as any
        }
        getRowClassName={(params) =>
          params.row.cancelled ? "cancelled-row" : ""
        }
      />
    </div>
  );
};
